import { useState } from 'react';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import FormHelperText from '@mui/material/FormHelperText';
import CircularProgress from '@mui/material/CircularProgress';
import { DataGrid, GridFooter, GridToolbar } from '@mui/x-data-grid';
import Radio from '@mui/material/Radio';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { format } from 'date-fns';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useMutation, useQuery } from 'react-query';
import PropTypes from 'prop-types';
import Slide from '@mui/material/Slide';
import PageLayout from '../../shared/PageLayout/PageLayout';
import { formatPhone } from '../../../utils/formatters';
import CustomLoading from '../../shared/CustomLoading';
import {
	GetServiceLocations,
	PutUpdateServiceLocation,
} from '../../../data/apiCalls';
import { QueryKey } from '../../../data/queryHooks/QueryKeys';
import useSessionContext from '../../../data/queryHooks/useSessionContext';
import { getHomePagePath } from '../../../scripts/navigationHelpers';
import { HOME_URL } from '../../../scripts/navigationItems';
import useIsMobile from '../../../hooks/useIsMobile';
import CustomNoResultsOverlay from '../../shared/CustomNoResultsOverlay';

const SlideLeft = props => <Slide {...props} direction='left' />;

const CenteredCell = ({ children }) => (
	<Stack justifyContent='center' sx={{ height: '100%', py: 2 }}>
		{children}
	</Stack>
);

CenteredCell.propTypes = {
	children: PropTypes.node,
};

const SelectLocation = () => {
	const context = useOutletContext();
	const location = useLocation();
	const navigate = useNavigate();
	const [selectionModel, setSelectionModel] = useState('');
	const [showErrorBanner, setShowErrorBanner] = useState(false);
	const [paginationModel, setPaginationModel] = useState({
		page: 0,
		pageSize: 10,
	});
	const { page, pageSize } = paginationModel;
	const [searchString, setSearchString] = useState('');
	const renderConfirmLocation =
		location?.pathname?.includes('confirmLocation');
	const {
		ashlinkUserId,
		ashlinkUserEntityType,
		providerId,
		clinicId,
		permissions,
		redirectionLinks,
	} = useSessionContext();
	const isMobile = useIsMobile();

	const {
		isLoading,
		isRefetching,
		data: queryData,
	} = useQuery(
		[QueryKey.GET_LOCATIONS, page, pageSize, searchString],
		() =>
			GetServiceLocations(
				ashlinkUserEntityType,
				ashlinkUserId,
				null,
				null,
				searchString,
				page + 1, // endpoint is 1 indexed, but data grid is 0 indexed
				pageSize ?? 10 // default to 10 if pageSize is not set
			),
		{
			staleTime: Infinity,
			refetchOnWindowFocus: false,
			keepPreviousData: true,
		}
	);

	const { mutate, isLoading: isFetching } = useMutation({
		mutationFn: values => PutUpdateServiceLocation(values),
		onSuccess: () => {
			context.setHasDefaultLocation(true);

			const homePath = getHomePagePath(permissions, redirectionLinks);

			if (!homePath || homePath?.includes('.aspx')) {
				window.open(homePath || HOME_URL, '_self', 'noreferrer');
			} else {
				navigate(homePath);
			}
		},
		onError: () => {
			setShowErrorBanner(true);
		},
	});

	const serviceLocations = queryData?.data?.data?.ashlinkUserProfiles ?? [];
	const totalRows = queryData?.data?.data?.totalRows ?? 0;

	const handleSubmit = values => {
		const data = {
			...values,
			AshlinkUserId: ashlinkUserId,
			AshlinkUserEntityType: ashlinkUserEntityType,
			ProviderId: providerId,
			ClinicId: clinicId,
		};

		if (showErrorBanner) {
			setShowErrorBanner(false);
		}
		mutate(data);
	};

	const validationSchema = Yup.object().shape({
		DrFedID: Yup.number().required('Please select a location'),
		SetDefault: Yup.bool(),
	});

	const checkRowId = row => `${row.drFedID}${row.profileID}${row.taxID}`;

	const columns = [
		{
			field: 'selected',
			headerName: 'Select',
			sortable: false,
			flex: 1,
			minWidth: 75,
			renderCell: e => (
				<CenteredCell>
					<Radio
						checked={checkRowId(selectionModel) === e.id}
						value={e.id}
						inputProps={{
							'aria-label': `${e.row.clinicName} at ${e.row.address1}`,
						}}
					/>
				</CenteredCell>
			),
		},
		{
			field: 'clinicName',
			headerName: 'Location',
			flex: 2,
			minWidth: 300,
			renderCell: params => <CenteredCell>{params.value}</CenteredCell>,
		},
		{
			field: 'address1',
			headerName: 'Address',
			flex: 4,
			minWidth: 300,
			renderCell: params => (
				<CenteredCell>
					<div>{params.row.address1}</div>
					<div>{params.row.address2}</div>
					<div>
						{params.row.city}, {params.row.state}{' '}
						{params.row.zipCode}
					</div>
				</CenteredCell>
			),
		},
		{
			field: 'info',
			headerName: 'Info',
			flex: 2,
			minWidth: 200,
			renderCell: params => (
				<CenteredCell>
					<span>
						<b>Phone: </b>
						{formatPhone(params.row.phone) || 'N/A'}
					</span>
					<span>
						<b>Fax: </b>
						{formatPhone(params.row.fax) || 'N/A'}
					</span>
					<span>
						<b>Tax ID#: </b>
						{params.row.taxID}
					</span>
				</CenteredCell>
			),
		},
		{
			field: 'drEffectiveDate',
			headerName: 'Effective',
			flex: 1,
			minWidth: 110,
			valueFormatter: value => {
				if (!value) return '';
				return format(new Date(value), 'MM/dd/yyyy');
			},
			renderCell: params => (
				<CenteredCell>{params.formattedValue}</CenteredCell>
			),
		},
		{
			field: 'DrTerminationDate',
			headerName: 'Termed',
			flex: 1,
			minWidth: 100,
			valueFormatter: value => {
				if (!value) return '';
				return format(new Date(value), 'MM/dd/yyyy');
			},
			renderCell: params => (
				<CenteredCell>{params.formattedValue}</CenteredCell>
			),
		},
	];

	return (
		<PageLayout
			pageTitle={
				renderConfirmLocation
					? 'CONFIRM YOUR LOCATION'
					: 'Switch Location'
			}
			renderBackButton={renderConfirmLocation}
		>
			<Paper
				elevation={0}
				role='main'
				sx={{ py: 2, px: { md: 4, lg: 4 } }}
			>
				<Formik
					initialValues={{
						DrFedID: '',
						ProfileID: '',
						SetDefault: renderConfirmLocation,
					}}
					validationSchema={validationSchema}
					onSubmit={handleSubmit}
				>
					{({ setFieldValue, values, touched, errors }) => (
						<Form>
							<Box sx={{ padding: `0 ${isMobile ? '10px' : 0}` }}>
								<Typography variant='body1' sx={{ mb: 5.5 }}>
									{renderConfirmLocation ? (
										'Please select your default location. This will be the default location each time you login. You will be able to change this later.'
									) : (
										<b>
											To view information for a specific
											location, please select the location
											below
										</b>
									)}
								</Typography>
								<Typography
									variant='subtitle2'
									component='label'
									gutterBottom
								>
									1. Select{' '}
									{renderConfirmLocation && 'Default'}{' '}
									Location
								</Typography>
							</Box>

							<DataGrid
								sx={{
									mt: 5,
									'.MuiDataGrid-toolbarContainer': {
										display: isMobile && 'block',
									},
								}}
								autoHeight
								rows={serviceLocations ?? []}
								rowCount={totalRows}
								loading={isLoading || isRefetching}
								columns={columns}
								getRowId={row => checkRowId(row)}
								getRowHeight={() => 'auto'}
								slots={{
									noRowsOverlay: CustomNoResultsOverlay,
									loadingOverlay: CustomLoading,
									footer: GridFooter,
									toolbar: GridToolbar,
								}}
								slotProps={{
									noRowsOverlay: {
										customFailureVerbiage:
											'No Locations Available',
									},
									toolbar: {
										showQuickFilter: true,
										printOptions: {
											disableToolbarButton: true,
										},
										csvOptions: {
											disableToolbarButton: true,
										},
										quickFilterProps: {
											debounceMs: 300,
										},
									},
								}}
								pagination
								paginationModel={paginationModel}
								onPaginationModelChange={setPaginationModel}
								pageSizeOptions={[10, 20, 50]}
								showToolbar
								hideFooterSelectedRowCount
								disableMultipleRowSelection
								disableColumnMenu
								disableColumnFilter
								disableColumnResize
								disableColumnSelector
								disableDensitySelector
								debounceMs={300}
								filterMode='server'
								paginationMode='server'
								onFilterModelChange={filter =>
									setSearchString(
										filter.quickFilterValues.join(' ')
									)
								}
								onCellClick={p => {
									const newSelectionModel =
										serviceLocations.find(
											x => checkRowId(x) === p.id
										);

									if (newSelectionModel) {
										setSelectionModel(newSelectionModel);
										setFieldValue(
											'DrFedID',
											newSelectionModel.drFedID
										);
										setFieldValue(
											'ProfileID',
											newSelectionModel.profileID
										);
									}
								}}
								initialState={{
									sorting: {
										sortModel: [
											{
												field: 'clinicName',
												sort: 'asc',
											},
										],
									},
								}}
							/>
							<FormHelperText
								error={
									touched.DrFedID && Boolean(errors.DrFedID)
								}
							>
								{touched.DrFedID && errors.DrFedID}
							</FormHelperText>
							<Stack
								alignItems='center'
								spacing={5}
								sx={{ mt: 5 }}
							>
								{!renderConfirmLocation && (
									<FormControlLabel
										control={
											<Checkbox
												checked={values.setDefault}
											/>
										}
										label='Set as Default Location'
										name='setDefault'
										onChange={event =>
											setFieldValue(
												'SetDefault',
												event.target.checked
											)
										}
									/>
								)}

								<Button
									variant='contained'
									type='submit'
									disabled={isFetching}
								>
									{renderConfirmLocation
										? 'Confirm'
										: 'Switch Location'}

									{isFetching && (
										<CircularProgress
											size={24}
											sx={{
												position: 'absolute',
												top: '50%',
												left: '50%',
												marginTop: '-12px',
												marginLeft: '-12px',
											}}
										/>
									)}
								</Button>
							</Stack>
						</Form>
					)}
				</Formik>
			</Paper>
			<Snackbar
				open={showErrorBanner}
				autoHideDuration={10000}
				onClose={() => setShowErrorBanner(false)}
				anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
				TransitionComponent={SlideLeft}
				sx={{
					boxShadow: 'rgba(0, 0, 0, 0.24) 0px 3px 8px',
				}}
			>
				<Alert severity='error'>
					<Typography fontWeight={700}>
						Sorry, something went wrong.
					</Typography>
					<Typography>
						We were unable to process your request.
					</Typography>
				</Alert>
			</Snackbar>
		</PageLayout>
	);
};

export default SelectLocation;
